import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn, Modal } from "../../components/UI";
import { ClinicalTabs, useClients, ClientSelect } from "./shared";
import { FLabel, FSelect } from "../engagement/FormControls";
import { SignModal } from "../../components/SignatureCanvas";
import { SignatureStamp } from "../../components/SignaturePad";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { Plus, Eye, Trash2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { VersionAccordion } from "../../components/VersionAccordion";
import { ExportCsvBtn } from "../../components/ExportCsvBtn";

const CF_COLS = 9;

const CONSENT_AMEND_FIELDS = [
  { key: "template_name", label: "Consent title" },
  { key: "body", label: "Consent body", type: "textarea", rows: 8 },
  { key: "witness_name", label: "Witness name" },
  { key: "notes", label: "Amendment notes", type: "textarea", rows: 2 },
];

export default function ConsentForms() {
  const clients = useClients();
  const [templates, setTemplates] = useState([]);
  const [consents, setConsents] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ client_id: "", template_id: "" });
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const preselect = searchParams.get("client");
    if (preselect) {
      setClientFilter(preselect);
      setNewForm((f) => ({ ...f, client_id: preselect }));
      if (searchParams.get("new") === "1") setNewOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [signTarget, setSignTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/consent-templates").then((r) => setTemplates(r.data)); }, []);
  const load = () => {
    const params = clientFilter ? { client_id: clientFilter } : {};
    api.get("/consent-forms", { params }).then((r) => { setConsents(r.data); setLoading(false); });
  };
  useEffect(load, [clientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTemplate = templates.find((t) => t.id === newForm.template_id);

  const create = async () => {
    if (!newForm.client_id || !newForm.template_id) { toast.error("Select a client and a consent template"); return; }
    setBusy(true);
    try {
      await api.post("/consent-forms", newForm);
      toast.success("Consent created - ready for signature");
      setNewOpen(false); setNewForm({ client_id: "", template_id: "" }); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const sign = async ({ name, image, witness_name }) => {
    setBusy(true);
    try {
      await api.put(`/consent-forms/${signTarget.id}/sign`, { name, image, witness_name });
      toast.success("Consent signed");
      setSignTarget(null); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/consent-forms/${confirmDelete.id}`);
      toast.success("Consent deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doRevoke = async () => {
    if (revokeReason.trim().length < 5) {
      toast.error("Revocation reason must be at least 5 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/consent-forms/${revokeTarget.id}/revoke`, { reason: revokeReason.trim() });
      toast.success("Consent revoked - original record preserved");
      setRevokeTarget(null); setRevokeReason(""); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Consent Forms" subtitle="Standard consents with in-app e-signature"
        action={<div className="flex items-center gap-2"><ExportCsvBtn resource="consent_forms" /><Btn data-testid="add-consent-btn" onClick={() => setNewOpen(true)}><Plus size={15} /> New Consent</Btn></div>} />
      <ClinicalTabs />
      <Card>
        <div className="flex flex-wrap gap-3 p-4">
          <FSelect data-testid="consent-client-filter" className="w-56" placeholder="All Clients" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect>
        </div>
        <Table headers={["", "Consent", "Client", "Status", "Created", "Signed", "Witness", "Lock / Amend", ""]}>
          {consents.map((c) => (
            <VersionAccordion key={c.id} record={c} kind="consent_form" colSpan={CF_COLS}
              primaryTestId={`consent-row-${c.id}`} rowClassName="hover:bg-[#FDFCF8] transition-colors"
              renderPrimary={({ expandToggle, versionBadge }) => (
                <>
                  <Td className="w-8">{expandToggle}</Td>
                  <Td className="font-medium text-[#3E2723]">
                    <span className="flex items-center gap-1.5">{c.template_name}{versionBadge}</span>
                  </Td>
                  <Td>{c.client_name}</Td>
                  <Td><Badge value={c.status} /></Td>
                  <Td>{dayjs(c.created_at).format("MM/DD/YYYY")}</Td>
                  <Td>{c.signature ? dayjs(c.signature.signed_at).format("MM/DD/YYYY") : "-"}</Td>
                  <Td>{c.witness_name || "-"}</Td>
                  <Td>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ClinicalRecordLockControls kind="consent_form"
                        testKind={`consent-${c.id}`}
                        record={c} onChange={load}
                        amendableFields={CONSENT_AMEND_FIELDS} />
                    </div>
                  </Td>
                  <Td>
                    <div className="flex gap-2 justify-end">
                      {c.status === "pending" && (
                        <button type="button" data-testid={`consent-sign-${c.id}`} aria-label="Sign" onClick={() => setSignTarget(c)}
                          className="h-8 px-3 rounded-full bg-[#4E342E] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#3E2723]"><PenLine size={12} /> Sign</button>
                      )}
                      <button type="button" data-testid={`consent-view-${c.id}`} aria-label="View" onClick={() => setViewTarget(c)}
                        className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                      {c.status === "signed" && (
                        <button type="button" data-testid={`consent-revoke-${c.id}`} aria-label="Revoke"
                          onClick={() => { setRevokeTarget(c); setRevokeReason(""); }}
                          title="Formally revoke this signed consent (record is preserved for HIPAA)"
                          className="h-8 px-3 rounded-full bg-[#FFEBEE] text-[#C62828] text-xs font-semibold flex items-center gap-1 hover:bg-[#FFCDD2]">Revoke</button>
                      )}
                      {c.status === "pending" && !c.locked && (
                        <button type="button" data-testid={`consent-delete-${c.id}`} aria-label="Delete" onClick={() => setConfirmDelete(c)}
                          className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </Td>
                </>
              )}
            />
          ))}
        </Table>
        {!loading && consents.length === 0 && <Empty text="No consent forms yet" />}
      </Card>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="New Consent" wide>
        <div className="space-y-4">
          <div><FLabel>Client</FLabel><ClientSelect clients={clients} value={newForm.client_id} onChange={(v) => setNewForm((f) => ({ ...f, client_id: v }))} testId="consent-new-client" /></div>
          <div><FLabel>Consent Template</FLabel>
            <FSelect data-testid="consent-new-template" value={newForm.template_id} onChange={(e) => setNewForm((f) => ({ ...f, template_id: e.target.value }))} placeholder="Select consent template">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FSelect>
          </div>
          {selectedTemplate && (
            <div className="max-h-56 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-sm text-[#4E342E] whitespace-pre-line" data-testid="consent-template-preview">
              {selectedTemplate.body}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn data-testid="consent-create-btn" onClick={create} disabled={busy}>{busy ? "Creating…" : "Create Consent"}</Btn>
          </div>
        </div>
      </Modal>

      <SignModal open={!!signTarget} onClose={() => setSignTarget(null)} onSign={sign} busy={busy} withWitness
        title={`Sign - ${signTarget?.template_name || ""}`} defaultName={signTarget?.client_name || ""} />

      <Modal open={!!viewTarget} onClose={() => setViewTarget(null)} title={viewTarget?.template_name || "Consent"} wide>
        {viewTarget && (
          <div className="space-y-4" data-testid="consent-view-modal">
            <div className="text-xs text-[#7B6157]">{viewTarget.client_name} · created {dayjs(viewTarget.created_at).format("MM/DD/YYYY")} · <Badge value={viewTarget.status} /></div>
            <div className="max-h-64 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-sm text-[#4E342E] whitespace-pre-line">{viewTarget.body}</div>
            {viewTarget.signature && (
              <div className="rounded-xl border border-[#E8F5E9] bg-[#F6FBF6] p-4">
                <SignatureStamp sig={viewTarget.signature} label={viewTarget.witness_name ? `Signed (Witness: ${viewTarget.witness_name})` : "Signed"} />
              </div>
            )}
            {viewTarget.amended_by_name && (
              <div className="rounded-xl border border-[#FFCC80] bg-[#FFF3E0] p-3" data-testid="consent-amend-banner">
                <div className="text-xs font-bold uppercase tracking-wide text-[#8D5A00] mb-1">Latest Amendment (v{viewTarget.version_count || 2})</div>
                <div className="text-xs text-[#5D4037]">
                  Amended by <strong>{viewTarget.amended_by_name}</strong> on {dayjs(viewTarget.amended_at).format("MMM D, YYYY h:mm A")}: {viewTarget.last_change_summary}
                </div>
              </div>
            )}
            {viewTarget.hipaa_summary && (
              <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] p-4" data-testid="consent-hipaa-summary">
                <div className="text-xs font-bold uppercase tracking-wide text-[#2E7D32] mb-1">HIPAA Disclosure Summary</div>
                <p className="text-xs leading-relaxed text-[#33691E]">{viewTarget.hipaa_summary}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Consent">
        <p className="text-sm text-[#6D4C41] mb-5">Delete pending &quot;<span className="font-semibold">{confirmDelete?.template_name}</span>&quot; for {confirmDelete?.client_name}?</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="consent-confirm-delete-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>

      <Modal open={!!revokeTarget} onClose={() => setRevokeTarget(null)} title="Revoke Consent" wide>
        {revokeTarget && (
          <div className="space-y-4" data-testid="consent-revoke-modal">
            <div className="rounded-xl border border-[#FFCDD2] bg-[#FFEBEE] p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-[#C62828] mb-1">Formal revocation under 45 CFR 164.508</div>
              <p className="text-xs leading-relaxed text-[#8B0000]">The signed record for <strong>{revokeTarget.template_name}</strong> ({revokeTarget.client_name}) is preserved. Revocation stops any future use of the authorization, appears on all portal views, and is logged to the audit trail.</p>
            </div>
            <div>
              <FLabel>Reason for revocation (required, 5+ characters)</FLabel>
              <textarea data-testid="consent-revoke-reason" rows={3} value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]"
                placeholder="e.g., Client requested withdrawal on today's phone call." />
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setRevokeTarget(null)}>Cancel</Btn>
              <Btn data-testid="consent-confirm-revoke-btn"
                onClick={doRevoke} disabled={busy || revokeReason.trim().length < 5}
                className="border-[#C62828] bg-[#C62828] text-white hover:bg-[#B71C1C]">
                {busy ? "Revoking…" : "Confirm Revocation"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
